import type { User } from '@/types';
import type { PayloadAction } from '@reduxjs/toolkit';

import { logoutAuth } from '@/api/webappApi';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { loadState, saveState } from './localStorage';

const AUTH_TOKEN_KEY = 'authToken';
const USER_REDUCER_NAME = 'user';

export const enum USER_REDUCER_STATUS {
  NOT_INITIATED = 'not_initiated',
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
}

type UserState = {
  status: USER_REDUCER_STATUS;
  error: unknown;
  user: User | null;
};

const defaultState: UserState = {
  status: USER_REDUCER_STATUS.NOT_INITIATED,
  error: null,
  user: null,
};

const loadStorageUser = () => {
  const persistedState = loadState(USER_REDUCER_NAME);

  if (persistedState) {
    const time = new Date().getTime() / 1000;

    if (persistedState.expireOn > time) {
      return persistedState;
    }
  }

  return defaultState;
};

export const logout = createAsyncThunk(`${USER_REDUCER_NAME}/logout`, async () => {
  saveState(USER_REDUCER_NAME, null);
  saveState(AUTH_TOKEN_KEY, null);
  await logoutAuth();
});

export const userSlice = createSlice({
  name: USER_REDUCER_NAME,
  initialState: loadStorageUser(),
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout.pending, (state) => {
      state.status = USER_REDUCER_STATUS.LOADING;
      state.user = null;
      state.error = null;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.status = USER_REDUCER_STATUS.NOT_INITIATED;
      state.user = null;
      state.error = null;
    });
  },
  selectors: {
    userStatus: (state: UserState): USER_REDUCER_STATUS => state.status,
    user: (state: UserState): User | null => state.user,
    isUserLogin: (state: UserState): boolean | null => state.user !== null,
  },
});

// ACTIONS
export const { setUser } = userSlice.actions;

// SELECTORS
export const { user, isUserLogin } = userSlice.selectors;

export default userSlice.reducer;
